import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Car, MapPin, ChevronRight, ChevronDown, TrendingUp, Zap, Shield, Heart, ClipboardCheck, ArrowRight, Star, Building2, Newspaper, HelpCircle, DollarSign, Tag, Percent, Filter, X } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [transmission, setTransmission] = useState('');
  const [bodyType, setBodyType] = useState('');
  const [province, setProvince] = useState('');
  const [fuelType, setFuelType] = useState('');
  const [mileage, setMileage] = useState('');
  const [condition, setCondition] = useState('');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [stats, setStats] = useState({ totalCars: 5000, totalDealers: 173 });
  const [news, setNews] = useState([]);

  useEffect(() => {
    document.title = 'AutoSearch SA | Find Used Cars, Insurance & Finance in South Africa';
    fetchStats();
    fetchNews();
    trackPageView();
  }, []);

  const trackPageView = async () => {
    try {
      const sessionId = sessionStorage.getItem('autosearchsa_session') || 'sess_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
      sessionStorage.setItem('autosearchsa_session', sessionId);
      const referrer = document.referrer ? `&referrer=${encodeURIComponent(document.referrer)}` : '';
      await axios.post(`${API}/analytics/pageview?path=${encodeURIComponent('/')}&session_id=${sessionId}${referrer}`);
    } catch (e) {}
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/scrape/status`);
      const total = res.data?.database?.total_listings || 5000;
      setStats(prev => ({ ...prev, totalCars: total }));
    } catch (e) {}
  };

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${API}/news`, { params: { limit: 4 } });
      setNews(res.data.articles || []);
    } catch (e) {}
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchQuery) params.set('query', searchQuery);
    if (selectedMake) params.set('make', selectedMake);
    if (selectedModel) params.set('model', selectedModel);
    if (priceRange) {
      const [min, max] = priceRange.split('-');
      if (min) params.set('min_price', min);
      if (max) params.set('max_price', max);
    }
    if (yearFrom) params.set('year_from', yearFrom);
    if (yearTo) params.set('year_to', yearTo);
    if (transmission) params.set('transmission', transmission);
    if (bodyType) params.set('body_type', bodyType);
    if (province) params.set('province', province);
    if (fuelType) params.set('fuel_type', fuelType);
    if (mileage) params.set('max_mileage', mileage);
    if (condition) params.set('condition', condition);
    navigate(`/results?${params.toString()}`);
  };

  const handleQuickSearch = (type) => {
    navigate(`/results?body_type=${type}`);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedMake('');
    setSelectedModel('');
    setPriceRange('');
    setYearFrom('');
    setYearTo('');
    setTransmission('');
    setBodyType('');
    setProvince('');
    setFuelType('');
    setMileage('');
    setCondition('');
  };

  const makes = ['Toyota', 'Volkswagen', 'Ford', 'BMW', 'Mercedes-Benz', 'Audi', 'Hyundai', 'Kia', 'Nissan', 'Mazda', 'Honda', 'Suzuki', 'Isuzu', 'Renault', 'Opel', 'Jeep', 'Land Rover', 'Porsche', 'Volvo', 'Haval', 'GWM', 'Chery', 'BYD', 'MG', 'JAC', 'Mahindra', 'Mitsubishi', 'Lexus', 'Mini', 'Peugeot', 'Citroen', 'Fiat', 'Alfa Romeo', 'Jaguar', 'TATA', 'Subaru'];

  const bodyTypes = [
    { name: 'SUV', icon: '🚙', image: 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=300&q=80' },
    { name: 'Sedan', icon: '🚗', image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=300&q=80' },
    { name: 'Hatchback', icon: '🚘', image: 'https://images.unsplash.com/photo-1609521263047-f8f205293f24?w=300&q=80' },
    { name: 'Double Cab', icon: '🛻', image: 'https://images.unsplash.com/photo-1559416523-140ddc3d238c?w=300&q=80' },
    { name: 'Bakkie', icon: '🚚', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=300&q=80' },
    { name: 'Crossover', icon: '🚐', image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=300&q=80' },
  ];

  const specials = [
    { title: 'Toyota Starlet 1.5 XS', price: 'R289,900', monthly: 'R4,450/pm', image: 'https://images.unsplash.com/photo-1590362891991-f776e747a588?w=400&q=80', dealer: 'Toyota SA' },
    { title: 'Suzuki Swift 1.2 GL', price: 'R219,900', monthly: 'R3,995/pm', image: 'https://images.unsplash.com/photo-1609521263047-f8f205293f24?w=400&q=80', dealer: 'Suzuki SA' },
    { title: 'Haval Jolion 1.5T', price: 'R399,950', monthly: 'R5,995/pm', image: 'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=400&q=80', dealer: 'Haval SA' },
    { title: 'Ford Ranger Wildtrak', price: 'R699,900', monthly: 'R10,995/pm', image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=80', dealer: 'Ford SA' },
  ];

  const activeFilters = [selectedMake, selectedModel, priceRange, yearFrom, transmission, bodyType, province, fuelType, mileage, condition].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section with Background */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?w=1920&q=80" 
            alt="Hero background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-gray-900/70 to-gray-900"></div>
        </div>

        <div className="container relative z-10 py-16 text-center">
          {/* Logo & Slogan */}
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-green-500 via-yellow-500 to-red-500 rounded-2xl flex items-center justify-center shadow-lg">
              <Car className="w-8 h-8 text-white" />
            </div>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Auto<span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-yellow-400 to-red-400">Search</span><span className="text-blue-400">SA</span>
          </h1>
          
          <p className="text-2xl md:text-3xl text-yellow-400 italic mb-4 font-light">
            "Need a new car? No need to look far."
          </p>
          
          <p className="text-lg text-gray-300 mb-10">
            Search {stats.totalCars.toLocaleString()}+ vehicles from {stats.totalDealers}+ trusted dealers across South Africa
          </p>

          <p className="text-base md:text-lg text-white/80 mb-8">
            Use our <Link to="/affordability" className="text-yellow-400 font-semibold hover:underline">Finance &amp; Affordability Calculators</Link> to estimate repayments and budget confidently before you buy.
          </p>

          {/* Main Search Card */}
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_280px] gap-6 items-stretch">
              
              {/* Left - Inspector Card */}
              <div className="bg-green-600/20 backdrop-blur-sm border border-green-500/30 rounded-2xl overflow-hidden text-left hidden lg:flex flex-col">
                {/* Image */}
                <div className="h-32 relative">
                  <img 
                    src="https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=400&q=80" 
                    alt="Car inspection" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-green-900/90 to-transparent"></div>
                  <div className="absolute bottom-3 left-3">
                    <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                      <ClipboardCheck className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
                {/* Content */}
                <div className="p-4 flex flex-col flex-grow justify-between">
                  <div>
                    <h3 className="text-xl md:text-2xl font-extrabold text-white mb-2">Hire an Inspector</h3>
                    <p className="text-gray-300 text-sm md:text-base mb-2">Don't buy a lemon! Get a professional 60-point vehicle inspection before you purchase.</p>
                    <p className="text-white/90 font-semibold text-sm md:text-base mb-2">Top benefits:</p>
                    <ul className="text-white/90 text-sm md:text-base font-medium space-y-1 list-disc list-inside">
                      <li>Avoid hidden faults</li>
                      <li>Know the true condition</li>
                      <li>Prevent costly repairs</li>
                      <li>Negotiate with confidence</li>
                      <li>Buy safely and confidently</li>
                    </ul>
                  </div>
                  <Link to="/inspector" className="text-green-400 font-bold flex items-center gap-1 hover:gap-2 transition-all text-base md:text-lg">
                    Book from R1,295 <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Center - Search Form */}
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <Search className="w-5 h-5 text-green-400" />
                    Find Your Dream Car
                  </h2>
                  {activeFilters > 0 && (
                    <button onClick={clearFilters} className="text-sm text-gray-400 hover:text-white flex items-center gap-1">
                      <X className="w-4 h-4" /> Clear
                    </button>
                  )}
                </div>

                <form onSubmit={handleSearch} className="space-y-3">
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search any car... (e.g. Toyota Hilux 2023)"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-3.5 bg-white rounded-xl text-gray-900 placeholder-gray-500 text-base"
                    />
                  </div>

                  {/* Main Filters - 3 columns */}
                  <div className="grid grid-cols-3 gap-2">
                    <select value={selectedMake} onChange={(e) => setSelectedMake(e.target.value)} className="px-3 py-3 bg-white rounded-xl text-gray-700 text-sm">
                      <option value="">Make</option>
                      {makes.map(make => <option key={make} value={make}>{make}</option>)}
                    </select>
                    <input type="text" placeholder="Model" value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} className="px-3 py-3 bg-white rounded-xl text-gray-700 placeholder-gray-500 text-sm" />
                    <select value={bodyType} onChange={(e) => setBodyType(e.target.value)} className="px-3 py-3 bg-white rounded-xl text-gray-700 text-sm">
                      <option value="">Body Type</option>
                      <option value="SUV">SUV</option>
                      <option value="Sedan">Sedan</option>
                      <option value="Hatchback">Hatchback</option>
                      <option value="Double Cab">Double Cab</option>
                      <option value="Bakkie">Bakkie</option>
                      <option value="Crossover">Crossover</option>
                      <option value="Coupe">Coupe</option>
                      <option value="Station Wagon">Station Wagon</option>
                      <option value="Minibus">Minibus</option>
                      <option value="Cabriolet">Cabriolet</option>
                    </select>
                  </div>

                  {/* Price & Year Row */}
                  <div className="grid grid-cols-4 gap-2">
                    <select value={priceRange} onChange={(e) => setPriceRange(e.target.value)} className="px-3 py-3 bg-white rounded-xl text-gray-700 text-sm">
                      <option value="">Price Range</option>
                      <option value="0-100000">Under R100k</option>
                      <option value="100000-200000">R100k - R200k</option>
                      <option value="200000-350000">R200k - R350k</option>
                      <option value="350000-500000">R350k - R500k</option>
                      <option value="500000-750000">R500k - R750k</option>
                      <option value="750000-1000000">R750k - R1M</option>
                      <option value="1000000-">R1M+</option>
                    </select>
                    <select value={yearFrom} onChange={(e) => setYearFrom(e.target.value)} className="px-3 py-3 bg-white rounded-xl text-gray-700 text-sm">
                      <option value="">Year From</option>
                      {[...Array(20)].map((_, i) => { const y = 2025 - i; return <option key={y} value={y}>{y}</option>; })}
                    </select>
                    <select value={yearTo} onChange={(e) => setYearTo(e.target.value)} className="px-3 py-3 bg-white rounded-xl text-gray-700 text-sm">
                      <option value="">Year To</option>
                      {[...Array(20)].map((_, i) => { const y = 2025 - i; return <option key={y} value={y}>{y}</option>; })}
                    </select>
                    <select value={mileage} onChange={(e) => setMileage(e.target.value)} className="px-3 py-3 bg-white rounded-xl text-gray-700 text-sm">
                      <option value="">Mileage</option>
                      <option value="10000">Under 10,000 km</option>
                      <option value="30000">Under 30,000 km</option>
                      <option value="50000">Under 50,000 km</option>
                      <option value="75000">Under 75,000 km</option>
                      <option value="100000">Under 100,000 km</option>
                      <option value="150000">Under 150,000 km</option>
                      <option value="200000">Under 200,000 km</option>
                    </select>
                  </div>

                  {/* More Filters Toggle */}
                  <button 
                    type="button" 
                    onClick={() => setShowMoreFilters(!showMoreFilters)}
                    className="w-full py-2 text-sm text-gray-300 hover:text-white flex items-center justify-center gap-2 transition"
                  >
                    <Filter className="w-4 h-4" />
                    {showMoreFilters ? 'Less Options' : 'More Options'}
                    <ChevronDown className={`w-4 h-4 transition-transform ${showMoreFilters ? 'rotate-180' : ''}`} />
                  </button>

                  {/* Extended Filters */}
                  {showMoreFilters && (
                    <div className="grid grid-cols-4 gap-2 pt-2 border-t border-white/10">
                      <select value={transmission} onChange={(e) => setTransmission(e.target.value)} className="px-3 py-3 bg-white rounded-xl text-gray-700 text-sm">
                        <option value="">Transmission</option>
                        <option value="Automatic">Automatic</option>
                        <option value="Manual">Manual</option>
                      </select>
                      <select value={fuelType} onChange={(e) => setFuelType(e.target.value)} className="px-3 py-3 bg-white rounded-xl text-gray-700 text-sm">
                        <option value="">Fuel Type</option>
                        <option value="Petrol">Petrol</option>
                        <option value="Diesel">Diesel</option>
                        <option value="Hybrid">Hybrid</option>
                        <option value="Electric">Electric</option>
                      </select>
                      <select value={condition} onChange={(e) => setCondition(e.target.value)} className="px-3 py-3 bg-white rounded-xl text-gray-700 text-sm">
                        <option value="">Condition</option>
                        <option value="New">New</option>
                        <option value="Demo">Demo</option>
                        <option value="Used">Used</option>
                      </select>
                      <select value={province} onChange={(e) => setProvince(e.target.value)} className="px-3 py-3 bg-white rounded-xl text-gray-700 text-sm">
                        <option value="">Province</option>
                        <option value="Gauteng">Gauteng</option>
                        <option value="Western Cape">Western Cape</option>
                        <option value="KwaZulu-Natal">KwaZulu-Natal</option>
                        <option value="Eastern Cape">Eastern Cape</option>
                        <option value="Free State">Free State</option>
                        <option value="Mpumalanga">Mpumalanga</option>
                        <option value="Limpopo">Limpopo</option>
                        <option value="North West">North West</option>
                        <option value="Northern Cape">Northern Cape</option>
                      </select>
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid="search-cars-btn"
                    className="w-full py-4 bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 text-white font-bold rounded-xl hover:opacity-90 transition flex items-center justify-center gap-2 text-lg shadow-lg"
                  >
                    <Search className="w-5 h-5" />
                    Search {stats.totalCars.toLocaleString()}+ Cars
                  </button>
                </form>

                {/* Quick Body Type Pills */}
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {['SUV', 'Sedan', 'Hatchback', 'Double Cab', 'Bakkie', 'Crossover'].map(type => (
                    <button key={type} onClick={() => handleQuickSearch(type)} className="px-3 py-1.5 bg-white/20 text-white text-sm rounded-full hover:bg-white/30 transition">
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Right - Life Cover Card */}
              <div className="bg-red-600/20 backdrop-blur-sm border border-red-500/30 rounded-2xl overflow-hidden text-left hidden lg:flex flex-col">
                {/* Image */}
                <div className="h-32 relative">
                  <img 
                    src="https://images.unsplash.com/photo-1591522811280-a8759970b03f?w=400&q=80" 
                    alt="Family protection" 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-red-900/90 to-transparent"></div>
                  <div className="absolute bottom-3 left-3">
                    <div className="w-10 h-10 bg-red-500 rounded-xl flex items-center justify-center">
                      <Heart className="w-5 h-5 text-white" />
                    </div>
                  </div>
                </div>
                {/* Content */}
                <div className="p-4 flex flex-col flex-grow justify-between">
                  <div>
                    <h3 className="text-xl md:text-2xl font-extrabold text-white mb-2">Life & Income Cover</h3>
                    <p className="text-gray-300 text-sm md:text-base mb-2">Protect your family's future. Get life cover & income protection if you can't work.</p>
                    <p className="text-white/90 font-semibold text-sm md:text-base mb-2">Top benefits:</p>
                    <ul className="text-white/90 text-sm md:text-base font-medium space-y-1 list-disc list-inside">
                      <li>Protect your income</li>
                      <li>Secure your family’s future</li>
                      <li>Cover debts &amp; monthly expenses</li>
                      <li>Protection if you can’t work</li>
                      <li>Peace of mind for the unexpected</li>
                    </ul>
                  </div>
                  <Link to="/life-cover" className="text-red-400 font-bold flex items-center gap-1 hover:gap-2 transition-all text-base md:text-lg">
                    Get Free Quote <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Features */}
      <section className="py-8 bg-gray-800">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Link to="/help-me-find" className="flex items-center gap-4 p-4 bg-gray-700/50 rounded-xl hover:bg-gray-700 transition">
              <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                <HelpCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white">Help Me Find</h3>
                <p className="text-sm text-gray-400">We search for you</p>
              </div>
            </Link>
            <Link to="/sell-your-car" className="flex items-center gap-4 p-4 bg-gray-700/50 rounded-xl hover:bg-gray-700 transition">
              <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white">Sell Your Car</h3>
                <p className="text-sm text-gray-400">Get the best price</p>
              </div>
            </Link>
            <Link to="/affordability" className="flex items-center gap-4 p-4 bg-gray-700/50 rounded-xl hover:bg-gray-700 transition">
              <div className="w-12 h-12 bg-yellow-600 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white">Finance Calculator</h3>
                <p className="text-sm text-gray-400">What can I afford?</p>
              </div>
            </Link>
            <Link to="/insurance" className="flex items-center gap-4 p-4 bg-gray-700/50 rounded-xl hover:bg-gray-700 transition">
              <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-white">Insurance Quote</h3>
                <p className="text-sm text-gray-400">Compare & save</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Car Specials Banner */}
      <section className="py-12 bg-gradient-to-r from-orange-600 to-red-600">
        <div className="container">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Percent className="w-8 h-8 text-white" />
              <h2 className="text-2xl font-bold text-white">Latest Car Specials</h2>
            </div>
            <Link to="/specials" className="flex items-center gap-1 text-white font-medium hover:underline">
              View All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {specials.map((special, idx) => (
              <Link key={idx} to="/specials" className="group bg-white rounded-xl overflow-hidden hover:shadow-2xl transition">
                <div className="relative h-40">
                  <img src={special.image} alt={special.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  <div className="absolute top-2 left-2">
                    <span className="px-2 py-1 bg-red-600 text-white text-xs font-bold rounded">SPECIAL</span>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-xs text-gray-500 mb-1">{special.dealer}</p>
                  <h3 className="font-bold text-gray-900 mb-2 group-hover:text-red-600 transition">{special.title}</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-lg font-bold text-red-600">{special.price}</span>
                    <span className="text-sm text-gray-500">or {special.monthly}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Shop by Body Type */}
      <section className="py-12 bg-gray-100 dark:bg-gray-900">
        <div className="container">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Car className="w-6 h-6 text-blue-500" />
            Shop Cars by Type
          </h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {bodyTypes.map((type) => (
              <button key={type.name} onClick={() => handleQuickSearch(type.name)} className="group relative h-36 rounded-xl overflow-hidden shadow-md">
                <img src={type.image} alt={type.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/20 group-hover:from-blue-900/80"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-center">
                  <span className="text-3xl mb-1 block">{type.icon}</span>
                  <span className="text-white font-bold">{type.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Shop by Budget */}
      <section className="py-12 bg-white dark:bg-gray-800">
        <div className="container">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-500" />
            Shop by Budget
          </h2>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Under R100k', range: '0-100000', color: 'from-green-500 to-green-600' },
              { label: 'R100k - R200k', range: '100000-200000', color: 'from-green-400 to-yellow-500' },
              { label: 'R200k - R350k', range: '200000-350000', color: 'from-yellow-400 to-yellow-500' },
              { label: 'R350k - R500k', range: '350000-500000', color: 'from-yellow-500 to-orange-500' },
              { label: 'R500k - R750k', range: '500000-750000', color: 'from-orange-500 to-red-500' },
              { label: 'R750k+', range: '750000-', color: 'from-red-500 to-red-600' },
            ].map((budget) => (
              <button
                key={budget.label}
                onClick={() => navigate(`/results?min_price=${budget.range.split('-')[0]}&max_price=${budget.range.split('-')[1] || ''}`)}
                className={`py-4 px-4 rounded-xl bg-gradient-to-r ${budget.color} text-white font-bold hover:opacity-90 hover:scale-105 transition shadow-md`}
              >
                {budget.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Shop by Brand */}
      <section className="py-12 bg-gray-100 dark:bg-gray-900">
        <div className="container">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Shop Cars by Brand</h2>
          
          <div className="flex flex-wrap gap-2">
            {makes.slice(0, 24).map((make) => (
              <button
                key={make}
                onClick={() => navigate(`/results?make=${make}`)}
                className="px-4 py-2 bg-white dark:bg-gray-800 rounded-full text-gray-700 dark:text-gray-300 hover:bg-blue-600 hover:text-white transition font-medium text-sm shadow-sm"
              >
                {make}
              </button>
            ))}
            <Link to="/results" className="px-4 py-2 bg-blue-600 rounded-full text-white font-medium text-sm flex items-center gap-1">
              Browse All <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Latest News & Reviews */}
      {news.length > 0 && (
        <section className="py-12 bg-white dark:bg-gray-800">
          <div className="container">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <Newspaper className="w-6 h-6 text-blue-500" />
                Latest Car News & Reviews
              </h2>
              <Link to="/news" className="flex items-center gap-1 text-blue-600 hover:underline font-medium">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {news.slice(0, 4).map((article, idx) => (
                <a key={article.id || idx} href={article.source_url || '/news'} target={article.source_url ? '_blank' : '_self'} rel="noopener noreferrer" className="group bg-gray-50 dark:bg-gray-700 rounded-xl overflow-hidden hover:shadow-lg transition">
                  <div className="relative h-40">
                    <img src={article.image || 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=400&q=80'} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded">{article.category}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-blue-600 transition">{article.title}</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{article.excerpt}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Services Section */}
      <section className="py-12 bg-gray-900">
        <div className="container">
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/inspector" className="group p-6 bg-gradient-to-br from-green-600/20 to-green-800/20 border border-green-500/30 rounded-2xl hover:border-green-500/60 transition">
              <div className="w-14 h-14 bg-green-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition">
                <ClipboardCheck className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Hire an Inspector</h3>
              <p className="text-gray-400 mb-4">Don't buy a lemon! Get a professional 60-point vehicle inspection.</p>
              <span className="text-green-400 font-semibold flex items-center gap-1">
                Book from R1,295 <ChevronRight className="w-4 h-4" />
              </span>
            </Link>

            <Link to="/life-cover" className="group p-6 bg-gradient-to-br from-red-600/20 to-red-800/20 border border-red-500/30 rounded-2xl hover:border-red-500/60 transition">
              <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Life & Income Cover</h3>
              <p className="text-gray-400 mb-4">Protect your family's future with life cover & income protection.</p>
              <span className="text-red-400 font-semibold flex items-center gap-1">
                Get Free Quote <ChevronRight className="w-4 h-4" />
              </span>
            </Link>

            <Link to="/ev-charging" className="group p-6 bg-gradient-to-br from-blue-600/20 to-blue-800/20 border border-blue-500/30 rounded-2xl hover:border-blue-500/60 transition">
              <div className="w-14 h-14 bg-blue-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">EV Charging Stations</h3>
              <p className="text-gray-400 mb-4">Going electric? Find charging stations across South Africa.</p>
              <span className="text-blue-400 font-semibold flex items-center gap-1">
                Find Stations <ChevronRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* Find Dealers CTA */}
      <section className="py-12 bg-gray-100 dark:bg-gray-800">
        <div className="container">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Find Dealers Near You</h2>
                <p className="text-blue-100">Browse {stats.totalDealers}+ dealerships across South Africa</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Link to="/dealers" className="px-6 py-3 bg-white text-blue-600 rounded-xl font-bold hover:bg-blue-50 transition flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Find Dealers
              </Link>
              <Link to="/dealer-benefits" className="px-6 py-3 bg-white/10 border border-white/30 text-white rounded-xl font-bold hover:bg-white/20 transition">
                List Your Dealership
              </Link>
            </div>
          </div>
        </div>
      </section>
        {/* SEO Content for Google */}
<section className="container mx-auto py-12 px-4 text-gray-400">
  <h2 className="text-2xl font-bold mb-4">
    Used Cars for Sale in South Africa
  </h2>

  <p className="mb-3">
    AutoSearch SA helps you search thousands of used cars for sale across South Africa. 
    Compare vehicles from dealerships and private sellers including Toyota, Volkswagen, 
    Ford, BMW, Mercedes-Benz, Audi, Nissan and more.
  </p>

  <p className="mb-3">
    Our platform allows you to compare vehicle prices, calculate car finance repayments, 
    estimate insurance costs, and explore vehicle inspection services before you buy.
  </p>

  <p className="mb-3">
    We also help South Africans compare financial protection products including life cover, 
    income protection insurance, vehicle insurance and personal financial planning tools.
  </p>

  <p>
    Start searching today to find the best deals on second hand cars in Johannesburg, 
    Cape Town, Durban and across South Africa.
  </p>
</section>
    </div>
  );
};

export default Home;
